import { computed, makeObservable, observable } from 'mobx';

import { delay } from 'utils/Async';
import { normalizeSpace, removeFilenameExtension } from 'utils/String';

import type TemplatesManager from './Templates';

export default class TemplateUploadManager {
    private readonly parent: TemplatesManager;
    public modalVisible = false;
    public templateName = '';
    private _file: File | undefined = undefined;
    public replaceAssets = true;

    // Set for the duration of upload() - this client's own upload, new or
    // update. For a new template it is all `loading` has to go on until
    // Templates.upload() has created the template document.
    private isOwnUploadInProgress = false;

    constructor(parent: TemplatesManager) {
        this.parent = parent;
        makeObservable<
            TemplateUploadManager,
            '_file' | 'isOwnUploadInProgress'
        >(this, {
            modalVisible: observable,
            templateName: observable,
            _file: observable.ref,
            replaceAssets: observable,
            isOwnUploadInProgress: observable,
            isUpdate: computed,
            loading: computed,
        });
    }

    public get file(): File | undefined {
        return this._file;
    }

    public set file(file: File | undefined) {
        this._file = file;
        this.templateName = file ? removeFilenameExtension(file.name) : '';
        this.replaceAssets = !this.isUpdate;
    }

    public get fileName(): string {
        return this._file?.name ?? '';
    }

    // True while this client is uploading, or while the template with the
    // modal's name is being processed - by anyone, which is what blocks
    // overlapping uploads to the same template.
    public get loading() {
        return this.isOwnUploadInProgress || !!this.findTemplate()?.loading;
    }

    // Whether the modal's name is an existing template's. Derived rather
    // than stored, so it stays current - without listening for it - as
    // templates arrive, change or go, e.g. another user's upload.
    public get isUpdate() {
        return !!this.findTemplate();
    }

    private findTemplate() {
        if (!this.templateName) return undefined;
        return this.parent.getByName(this.templateName);
    }

    // Prepares the modal to update the existing template with this name.
    public beginUpdate(name: string) {
        this.reset();
        this.templateName = name;
        this.replaceAssets = false;
    }

    public async upload() {
        if (!this._file) return;
        this.isOwnUploadInProgress = true;
        this.templateName = normalizeSpace(this.templateName);
        try {
            await this.parent.upload(
                this.templateName,
                this._file,
                this.isUpdate,
                this.replaceAssets
            );
        } finally {
            this.isOwnUploadInProgress = false;
        }
        void this.hideModalAndReset();
    }

    public async hideModalAndReset() {
        this.modalVisible = false;
        await delay(250);
        this.reset();
    }

    public reset() {
        this.templateName = '';
        this._file = undefined;
        this.replaceAssets = true;
        this.isOwnUploadInProgress = false;
        this.modalVisible = false;
    }
}
